import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Config from 'Config';
import '../LoginView/LoginView.scss';
import './LiteView.scss';
import {
  benchmarkModalByStep,
  cloudEnvironmentOptions,
  createInitialFormState,
  industryOptions,
  multicloudDriverOptions,
  progressSteps,
  regionOptions,
  strategicGoalOptions,
  userScaleOptions,
  welcomeHighlights,
} from './constants';
import BenchmarkModal from './components/BenchmarkModal';
import ChoiceCard from './components/ChoiceCard';
import CompletionCard from './components/CompletionCard';
import ContactForm from './components/ContactForm';
import GeographyQuestion from './components/GeographyQuestion';
import HeadquarterQuestion from './components/HeadquarterQuestion';
import HeroCard from './components/HeroCard';
import QuestionCard from './components/QuestionCard';
import SliderQuestion from './components/SliderQuestion';
import StepNavigation from './components/StepNavigation';
import { resetLiteHeaderState, setLiteHeaderState } from './liteHeaderStore';
import { isEmailValid } from 'utils/emails';
import { toast } from 'utils/Toast';
import cn from 'classnames';

const LiteView = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);
  const [isSavingLiteForm, setIsSavingLiteForm] = useState(false);
  const [endSelection, setEndSelection] = useState(false);
  const [formData, setFormData] = useState(() => createInitialFormState());
  const [isBenchmarkModalOpen, setIsBenchmarkModalOpen] = useState(false);
  const [benchmarkModalType, setBenchmarkModalType] = useState(null);
  const liteBodyRef = useRef(null);
  const benchmarkModalRef = useRef(null);
  const [modalPaddingBottom, setModalPaddingBottom] = useState(null);

  const totalSteps = progressSteps.length;
  const questionCount = totalSteps - 1;
  const progressPercent = Math.round((currentStep / (totalSteps - 1)) * 100);
  const currentStepKey = progressSteps[currentStep]?.key;

  const sliderValue = useMemo(() => (formData.providerCount > 100 ? '100+' : formData.providerCount), [formData.providerCount]);
  const highRegionOptions = useMemo(() => regionOptions.filter((option) => option.value !== formData.regions.highest), [formData.regions.highest]);
  const emergingRegionOptions = useMemo(
    () => regionOptions.filter((option) => option.value !== formData.regions.highest && !formData.regions.high.includes(option.value)),
    [formData.regions.highest, formData.regions.high]
  );

  const setSingleValue = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleInputChange = useCallback(
    (field) => (value) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const toggleSelection = useCallback((field, value) => {
    setFormData((prev) => {
      const exists = prev[field].includes(value);
      const nextValue = exists ? prev[field].filter((option) => option !== value) : [...prev[field], value];
      return { ...prev, [field]: nextValue };
    });
  }, []);

  const handleRegionChange = useCallback(
    (key, options = {}) => (selectionOrEvent) => {
      if (options.isMulti) {
        const selections = Array.isArray(selectionOrEvent) ? selectionOrEvent : selectionOrEvent ? [selectionOrEvent] : [];
        const values = selections.map((option) => option.value);

        setFormData((prev) => {
          const updatedRegions = { ...prev.regions, [key]: values };

          if (key === 'high') {
            updatedRegions.emerging = prev.regions.emerging.filter((value) => !values.includes(value));
          }

          return { ...prev, regions: updatedRegions };
        });
        return;
      }

      const value = selectionOrEvent?.target ? selectionOrEvent.target.value : selectionOrEvent?.value || '';
      setFormData((prev) => {
        const filteredHigh = prev.regions.high.filter((region) => region !== value);
        const filteredEmerging = prev.regions.emerging.filter((region) => region !== value);

        return {
          ...prev,
          regions: {
            ...prev.regions,
            [key]: value,
            high: filteredHigh,
            emerging: filteredEmerging,
          },
        };
      });
    },
    []
  );

  const isStepComplete = useMemo(() => {
    switch (currentStepKey) {
      case 'welcome':
        return true;
      case 'cloudProviders':
        return formData.providerCount !== null;
      case 'cloudEnvironments':
        return formData.cloudEnvironments.length > 0;
      case 'multicloudDrivers':
        return formData.factors.length > 0;
      case 'industry':
        return !!formData.ecosystemDensity;
      case 'userScale':
        return !!formData.userScale;
      case 'geography':
        return !!formData.regions.highest && formData.regions.emerging.length > 0;
      case 'strategicGoals':
        return formData.strategicGoals.length > 0;
      case 'headquarter':
        return !!formData.headquarter;
      case 'contact':
        return (
          !!formData.contactEmail &&
          !!formData.contactFirstName &&
          !!formData.contactName &&
          !!formData.contactJobTitle &&
          !!formData.contactCompany &&
          !!formData.contactCountry &&
          !!formData.consent
        );
      default:
        return false;
    }
  }, [currentStepKey, formData]);

  const closeBenchmarkModal = useCallback(() => {
    setIsBenchmarkModalOpen(false);
    setBenchmarkModalType(null);
  }, []);

  const goToNextStep = useCallback(() => {
    if (!isStepComplete) {
      return;
    }
    closeBenchmarkModal();
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  }, [closeBenchmarkModal, isStepComplete, totalSteps]);

  const goToPreviousStep = () => {
    closeBenchmarkModal();
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleContinue = () => {
    if (!isStepComplete) return;
    if (currentStepKey === 'industry' && (formData.ecosystemDensity === 'cloud' || formData.ecosystemDensity === 'network')) {
      closeBenchmarkModal();
      setEndSelection(true);
      setCurrentStep(totalSteps - 1);
      setIsCompleted(true);
      return;
    }
    const modalType = benchmarkModalByStep[currentStepKey];
    if (modalType) {
      if (!isBenchmarkModalOpen || benchmarkModalType !== modalType) {
        setBenchmarkModalType(modalType);
        setIsBenchmarkModalOpen(true);
        return;
      }
    }
    goToNextStep();
  };

  const handleSubmit = async () => {
    if (!isEmailValid(formData.contactEmail)) {
      toast('Email address is invalid', 'error');
      return;
    }

    try {
      setEndSelection(false);
      setHasSaveError(false);
      setIsSavingLiteForm(true);
      const params = { ...formData };
      delete params.consent;
      const response = await fetch(`${Config.API_URL}connectivity-advisor/save`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Failed to save Lite form: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving form:', error);
      setHasSaveError(true);
    } finally {
      setIsSavingLiteForm(false);
      setCurrentStep(totalSteps - 1);
      setIsCompleted(true);
    }
  };

  const handleEndSelectionBack = useCallback(() => {
    setIsCompleted(false);
    setEndSelection(false);
    const industryIndex = progressSteps.findIndex((s) => s.key === 'industry');
    setCurrentStep(industryIndex);
  }, []);

  const questionEyebrow = () => {
    if (currentStepKey === 'contact') return 'Final Step';
    if (currentStep === 0) return '';
    return `Question ${currentStep} of ${questionCount}`;
  };

  const renderStepContent = () => {
    if (isCompleted) {
      return <CompletionCard hasSaveError={hasSaveError} endSelection={endSelection} onBack={endSelection ? handleEndSelectionBack : undefined} />;
    }

    if (currentStep === 0) {
      return <HeroCard highlights={welcomeHighlights} />;
    }

    const eyebrow = questionEyebrow();

    switch (currentStepKey) {
      case 'cloudProviders':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="How many cloud and SaaS providers does your organization use?"
            description="This helps us understand the complexity of your current infrastructure to interconnect.">
            <SliderQuestion value={formData.providerCount} displayValue={sliderValue} onChange={(value) => setSingleValue('providerCount', value)} />
          </QuestionCard>
        );
      case 'cloudEnvironments':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="What are the most critical cloud environments for your business?"
            description="Select all that apply to your current infrastructure setup.">
            <div className="choice-list">
              {cloudEnvironmentOptions.map((option) => (
                <ChoiceCard
                  key={option.value}
                  option={option}
                  isSelected={formData.cloudEnvironments.includes(option.value)}
                  onSelect={() => toggleSelection('cloudEnvironments', option.value)}
                />
              ))}
            </div>
          </QuestionCard>
        );
      case 'multicloudDrivers':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="What factors are driving your hybrid multicloud approach?"
            description="This helps us understand your priorities regarding performance, cost and data management.">
            <div className="choice-list">
              {multicloudDriverOptions.map((option) => (
                <ChoiceCard
                  key={option.value}
                  option={option}
                  isSelected={formData.factors.includes(option.value)}
                  onSelect={() => toggleSelection('factors', option.value)}
                />
              ))}
            </div>
          </QuestionCard>
        );
      case 'industry':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="What is your organization's primary industry vertical?"
            description="This allows us to provide industry-specific recommendations.">
            <div className="choice-list">
              {industryOptions.map((option) => (
                <ChoiceCard
                  key={option.value}
                  option={option}
                  variant="radio"
                  isSelected={formData.ecosystemDensity === option.value}
                  onSelect={() => setSingleValue('ecosystemDensity', option.value)}
                />
              ))}
            </div>
          </QuestionCard>
        );
      case 'userScale':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="How many users do you support across your infrastructure?"
            description="Include both internal employees and external customers or partners.">
            <div className="choice-list">
              {userScaleOptions.map((option) => (
                <ChoiceCard
                  key={option.value}
                  option={option}
                  variant="radio"
                  isSelected={formData.userScale === option.value}
                  onSelect={() => setSingleValue('userScale', option.value)}
                />
              ))}
            </div>
          </QuestionCard>
        );
      case 'geography':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="Where are your users located globally?"
            description="Help us understand your user distribution to optimize your infrastructure strategy.">
            <GeographyQuestion
              regions={formData.regions}
              regionOptions={regionOptions}
              highRegionOptions={highRegionOptions}
              emergingRegionOptions={emergingRegionOptions}
              onRegionChange={handleRegionChange}
            />
          </QuestionCard>
        );
      case 'strategicGoals':
        return (
          <QuestionCard eyebrow={eyebrow} title="What are your primary strategic goals?" description="Select your top priorities for the next 12 months.">
            <div className="choice-list">
              {strategicGoalOptions.map((option) => (
                <ChoiceCard
                  key={option.value}
                  option={option}
                  isSelected={formData.strategicGoals.includes(option.value)}
                  onSelect={() => toggleSelection('strategicGoals', option.value)}
                />
              ))}
            </div>
          </QuestionCard>
        );
      case 'headquarter':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="Where is your organization headquartered?"
            description="This helps us understand your primary regulatory and business environment.">
            <HeadquarterQuestion value={formData.headquarter} onChange={handleInputChange('headquarter')} />
          </QuestionCard>
        );
      case 'contact':
        return (
          <QuestionCard
            eyebrow={eyebrow}
            title="Get your personalized report"
            description="Complete your assessment and receive actionable insights and recommendations tailored to your organization.">
            <ContactForm formData={formData} onChange={handleInputChange} />
          </QuestionCard>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isCompleted) {
      resetLiteHeaderState();
    } else {
      setLiteHeaderState({
        stepLabel: progressSteps[currentStep]?.label || '',
        stepNumber: currentStep + 1,
        totalSteps,
        progressPercent,
      });
    }
  }, [currentStep, isCompleted, progressPercent, totalSteps]);

  useEffect(
    () => () => {
      resetLiteHeaderState();
    },
    []
  );

  useEffect(() => {
    if (!isBenchmarkModalOpen || !liteBodyRef.current || !benchmarkModalRef.current) {
      return;
    }

    const scrollContainer = liteBodyRef.current;
    const modalSection = benchmarkModalRef.current;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const paddingTop = parseFloat(window.getComputedStyle(scrollContainer).paddingTop) || 0;
        const modalCard = modalSection.querySelector('.information-modal') || modalSection;
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const modalTop = modalCard.getBoundingClientRect().top;
        const extraOffset = 80;
        const offset = modalTop - containerTop - paddingTop - extraOffset;
        const nextTop = Math.max(scrollContainer.scrollTop + offset, 0);
        scrollContainer.scrollTo({ top: nextTop, behavior: 'smooth' });
      });
    });
  }, [isBenchmarkModalOpen]);

  useEffect(() => {
    if (!liteBodyRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      if (liteBodyRef.current) {
        liteBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }, [currentStep]);

  useEffect(() => {
    if (!isBenchmarkModalOpen) {
      setModalPaddingBottom(null);
      return;
    }

    let frameId;
    const updatePadding = () => {
      const container = benchmarkModalRef.current;
      if (!container) {
        return;
      }

      const modal = container.querySelector('.information-modal');
      if (!modal) {
        return;
      }

      const modalHeight = modal.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;
      setModalPaddingBottom(Math.max(viewportHeight - modalHeight, 0));
    };

    frameId = requestAnimationFrame(updatePadding);
    window.addEventListener('resize', updatePadding);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      window.removeEventListener('resize', updatePadding);
    };
  }, [benchmarkModalType, isBenchmarkModalOpen]);

  const liteContentStyle = modalPaddingBottom === null ? undefined : { '--lite-content-bottom-padding': `${modalPaddingBottom}px` };

  return (
    <div className={cn('content-login-view lite-view', { initial: currentStep === 0 })}>
      <div className="lite-shell">
        <main className="lite-body" ref={liteBodyRef}>
          <div className="lite-content" style={liteContentStyle}>
            {renderStepContent()}
            <div className={cn('benchmark-modal-section', { 'is-open': isBenchmarkModalOpen })} ref={benchmarkModalRef}>
              <BenchmarkModal isOpen={isBenchmarkModalOpen} modalType={benchmarkModalType} formData={formData} sliderValue={sliderValue} />
            </div>
          </div>
        </main>
        {!isCompleted && (
          <footer className="lite-footer">
            <StepNavigation
              currentStep={currentStep}
              totalSteps={totalSteps}
              isStepComplete={isStepComplete}
              onPrevious={goToPreviousStep}
              onContinue={handleContinue}
              onSubmit={handleSubmit}
              isSubmitting={isSavingLiteForm}
            />
          </footer>
        )}
      </div>
    </div>
  );
};

export default LiteView;
